import { type InputHTMLAttributes, forwardRef, useId } from 'react';

export interface InputProps extends InputHTMLAttributes<HTMLInputElement> {
  label: string;
  error?: string;
  hint?: string;
  success?: boolean;
}

export const Input = forwardRef<HTMLInputElement, InputProps>(
  ({ label, error, hint, success, id, disabled, className = '', ...props }, ref) => {
    const generatedId = useId();
    const inputId = id ?? generatedId;
    const errorId = `${inputId}-error`;
    const hintId = `${inputId}-hint`;

    return (
      <div className="flex w-full flex-col gap-1.5 text-left">
        <label htmlFor={inputId} className="text-xs font-semibold uppercase tracking-wider text-brand-offWhite/80">
          {label}
        </label>

        <div className="relative">
          <input
            ref={ref}
            id={inputId}
            disabled={disabled}
            aria-invalid={Boolean(error)}
            aria-describedby={error ? errorId : hint ? hintId : undefined}
            className={`w-full rounded-xl border bg-navy-light/60 px-4 py-3 text-sm text-white placeholder-brand-offWhite/40 shadow-inner transition-all duration-150 focus:outline-none min-h-[44px] ${
              error
                ? 'border-feedback-danger focus:border-feedback-danger focus:ring-2 focus:ring-feedback-danger/30'
                : success
                  ? 'border-feedback-success focus:border-feedback-success focus:ring-2 focus:ring-feedback-success/30'
                  : 'border-white/10 hover:border-white/30 focus:border-brand-orange focus:ring-2 focus:ring-brand-orange/30'
            } ${disabled ? 'cursor-not-allowed opacity-50' : ''} ${className}`}
            {...props}
          />

          {success && !error && (
            <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-3 text-feedback-success">
              <svg className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                <path
                  fillRule="evenodd"
                  d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                  clipRule="evenodd"
                />
              </svg>
            </div>
          )}
        </div>

        {error && (
          <p id={errorId} role="alert" className="text-xs font-medium text-feedback-danger">
            {error}
          </p>
        )}

        {hint && !error && (
          <p id={hintId} className="text-xs text-brand-offWhite/60">
            {hint}
          </p>
        )}
      </div>
    );
  },
);

Input.displayName = 'Input';
